import jsPDF from 'jspdf';
import moment from 'moment';
import { Button } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import aspireLogo from '../../../../assets/img/aspire-logo.png';
import './ExportToPdf.scss';

const COLORS = {
  primary: [0, 87, 146],
  primaryLight: [232, 242, 250],
  secondary: [44, 118, 191],
  text: [33, 37, 41],
  muted: [108, 117, 125],
  border: [210, 220, 230],
  white: [255, 255, 255],
  cardBg: [248, 250, 252],
  accent: [103, 186, 224]
};

const formatDate = value => (value ? moment(value).format('DD-MMM-YYYY') : '—');

const parseMultiSelectValue = questionValue => {
  try {
    const parsed = JSON.parse(questionValue || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const formatAnswer = question => {
  switch (question?.questionType) {
    case 'text':
    case 'rating':
      return question?.questionValue || '—';
    case 'multiSelect':
    default: {
      const selected = parseMultiSelectValue(question?.questionValue);
      return selected.length ? selected.join(', ') : '—';
    }
  }
};

const loadImageDataUrl = src =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve({
        dataUrl: canvas.toDataURL('image/png'),
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    };
    img.onerror = reject;
    img.src = src;
  });

const buildFeedbackPdf = async (feedbackResponse, fileName) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const marginX = 16;
  const marginTop = 18;
  const marginBottom = 22;
  const contentWidth = pageWidth - marginX * 2;
  let y = marginTop;
  let logoData = null;

  try {
    logoData = await loadImageDataUrl(aspireLogo);
  } catch {
    logoData = null;
  }

  const ensureSpace = needed => {
    if (y + needed > pageHeight - marginBottom) {
      pdf.addPage();
      y = marginTop;
    }
  };

  const writeWrapped = (text, options = {}) => {
    const {
      fontSize = 11,
      fontStyle = 'normal',
      color = COLORS.text,
      lineHeight = 6,
      indent = 0,
      maxWidth = contentWidth
    } = options;
    pdf.setFont('helvetica', fontStyle);
    pdf.setFontSize(fontSize);
    pdf.setTextColor(...color);
    const lines = pdf.splitTextToSize(String(text ?? ''), maxWidth - indent);
    lines.forEach(line => {
      ensureSpace(lineHeight);
      pdf.text(line, marginX + indent, y);
      y += lineHeight;
    });
  };

  // Brand header: light band for logo readability + Aspire accent stripe
  pdf.setFillColor(...COLORS.white);
  pdf.rect(0, 0, pageWidth, 26, 'F');
  pdf.setFillColor(...COLORS.primary);
  pdf.rect(0, 26, pageWidth, 3, 'F');
  pdf.setFillColor(...COLORS.accent);
  pdf.rect(0, 29, pageWidth, 1.2, 'F');

  if (logoData) {
    const logoHeight = 11;
    const logoWidth = (logoData.width / logoData.height) * logoHeight;
    pdf.addImage(logoData.dataUrl, 'PNG', marginX, 7.5, logoWidth, logoHeight);
  } else {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(...COLORS.primary);
    pdf.text('Aspire', marginX, 16);
  }

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.setTextColor(...COLORS.primary);
  pdf.text('Feedback Report', pageWidth - marginX, 16, { align: 'right' });
  y = 40;

  writeWrapped(feedbackResponse?.employeeName || 'Feedback Details', {
    fontSize: 16,
    fontStyle: 'bold',
    color: COLORS.primary,
    lineHeight: 8
  });
  writeWrapped('Employee feedback summary', {
    fontSize: 10,
    color: COLORS.muted,
    lineHeight: 5
  });
  y += 4;

  const metaItems = [
    ['Requested from', feedbackResponse?.requestedName || '—'],
    ['Email', feedbackResponse?.requestedEmail || '—'],
    ['Template', feedbackResponse?.templateName || '—'],
    ['Requested on', formatDate(feedbackResponse?.createdAt)],
    feedbackResponse?.status === 'completed'
      ? ['Completed on', formatDate(feedbackResponse?.updatedAt)]
      : null,
    ['Status', feedbackResponse?.status || '—']
  ].filter(Boolean);

  const metaPadding = 4;
  const colGap = 6;
  const colWidth = (contentWidth - colGap) / 2;
  const valueMaxWidth = colWidth - metaPadding * 2;

  // Measure metadata card height (two columns)
  let leftH = metaPadding + 4;
  let rightH = metaPadding + 4;
  metaItems.forEach(([label, value], index) => {
    const valueLines = pdf.splitTextToSize(String(value), valueMaxWidth);
    const blockH = 4 + valueLines.length * 4.5 + 3;
    if (index % 2 === 0) {
      leftH += blockH;
    } else {
      rightH += blockH;
    }
  });
  const metaHeight = Math.max(leftH, rightH) + 2;

  ensureSpace(metaHeight + 4);
  const metaStartY = y;

  pdf.setDrawColor(...COLORS.border);
  pdf.setFillColor(...COLORS.primaryLight);
  pdf.setLineWidth(0.4);
  pdf.roundedRect(marginX, metaStartY, contentWidth, metaHeight, 2, 2, 'FD');

  let leftY = metaStartY + metaPadding + 4;
  let rightY = metaStartY + metaPadding + 4;
  metaItems.forEach(([label, value], index) => {
    const isLeft = index % 2 === 0;
    const x = marginX + metaPadding + (isLeft ? 0 : colWidth + colGap);
    let rowY = isLeft ? leftY : rightY;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(...COLORS.muted);
    pdf.text(String(label).toUpperCase(), x, rowY);
    rowY += 4;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(...COLORS.text);
    const valueLines = pdf.splitTextToSize(String(value), valueMaxWidth);
    valueLines.forEach(line => {
      pdf.text(line, x, rowY);
      rowY += 4.5;
    });
    rowY += 3;

    if (isLeft) {
      leftY = rowY;
    } else {
      rightY = rowY;
    }
  });

  y = metaStartY + metaHeight + 8;

  writeWrapped('Responses', {
    fontSize: 12,
    fontStyle: 'bold',
    color: COLORS.primary,
    lineHeight: 6
  });
  y += 2;

  const questions = feedbackResponse?.questionsFeedbackResponse || [];
  questions.forEach((question, index) => {
    const questionText = `${index + 1}. ${question?.questionDescription || 'Question'}`;
    const answerText = formatAnswer(question);

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    const qLines = pdf.splitTextToSize(questionText, contentWidth - 10);
    pdf.setFont('helvetica', 'normal');
    const aLines = pdf.splitTextToSize(answerText, contentWidth - 10);
    const blockHeight = 6 + qLines.length * 5 + 2 + aLines.length * 5 + 6;

    ensureSpace(blockHeight);

    const blockY = y;
    pdf.setFillColor(...COLORS.cardBg);
    pdf.setDrawColor(...COLORS.border);
    pdf.setLineWidth(0.3);
    pdf.roundedRect(marginX, blockY, contentWidth, blockHeight, 2, 2, 'FD');

    pdf.setFillColor(...COLORS.secondary);
    pdf.rect(marginX, blockY, 1.5, blockHeight, 'F');

    y = blockY + 6;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(...COLORS.primary);
    qLines.forEach(line => {
      pdf.text(line, marginX + 5, y);
      y += 5;
    });

    y += 2;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(...COLORS.text);
    aLines.forEach(line => {
      pdf.text(line, marginX + 5, y);
      y += 5;
    });

    y = blockY + blockHeight + 4;
  });

  const pageCount = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    pdf.setPage(i);
    pdf.setDrawColor(...COLORS.border);
    pdf.setLineWidth(0.3);
    pdf.line(marginX, pageHeight - 14, pageWidth - marginX, pageHeight - 14);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(...COLORS.muted);
    pdf.text('Aspire', marginX, pageHeight - 8);
    pdf.text(`Page ${i} of ${pageCount}`, pageWidth - marginX, pageHeight - 8, {
      align: 'right'
    });
  }

  pdf.save(fileName);
};

const ExportToPDF = props => {
  const { fileName, feedbackResponse } = props;

  const exportToPDF = async () => {
    if (!feedbackResponse) {
      return;
    }
    await buildFeedbackPdf(feedbackResponse, fileName);
  };

  return (
    <Button
      id="export-btn"
      variant="text"
      onClick={exportToPDF}
      className="custom-export-btn"
      startIcon={<PictureAsPdfIcon />}
      disabled={!feedbackResponse}
    >
      Export
    </Button>
  );
};

export default ExportToPDF;
